import { Head, Link, router } from "@inertiajs/react"
import { format, formatDistanceToNow } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { Badge, StatusBadge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Notice } from "@/components/ui/notice"
import { Field, Fields, Panel } from "@/components/ui/panel"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminEnquiriesPath,
  adminSchoolPath,
  answerAdminEnquiryPath,
  closeAdminEnquiryPath,
  confirmMatchAdminEnquiryPath,
} from "@/routes"

import { type EnquiryDetail, type MatchCandidate, STATUS_COLOR } from "./types"

interface Props {
  enquiry: EnquiryDetail
  candidates: MatchCandidate[]
  matched_school: {
    id: number
    name: string | null
    has_history: boolean
  } | null
}

const stamp = (iso: string | null) =>
  iso ? format(new Date(iso), "d MMM yyyy 'at' HH:mm") : "—"

export default function AdminEnquiriesShow({
  enquiry,
  candidates,
  matched_school,
}: Props) {
  const answer = () =>
    router.post(
      answerAdminEnquiryPath(enquiry.id),
      {},
      { preserveScroll: true },
    )
  const close = () => router.post(closeAdminEnquiryPath(enquiry.id))
  const confirm = (salesforceAccountId: string) =>
    router.post(
      confirmMatchAdminEnquiryPath(enquiry.id),
      { salesforce_account_id: salesforceAccountId },
      { preserveScroll: true },
    )

  return (
    <AdminLayout>
      <Head title={`Enquiry · ${enquiry.name ?? enquiry.email ?? "unknown"}`} />
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[
            { label: "call list" },
            { label: "enquiries", href: adminEnquiriesPath() },
            { label: enquiry.name ?? "Someone" },
          ]}
          stamp={
            <>
              arrived{" "}
              {formatDistanceToNow(new Date(enquiry.received_at), {
                addSuffix: true,
              })}
            </>
          }
          title={enquiry.name ?? "Someone"}
        >
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tier={STATUS_COLOR[enquiry.status]}>
              {enquiry.status}
            </StatusBadge>
            <Badge type="modern" color="gray" size="sm">
              {enquiry.source.replace(/_/g, " ")}
            </Badge>
          </div>
        </PageHeader>

        {!enquiry.first_response_at && (
          <Notice tone="caution" role="status">
            {enquiry.sla_breached
              ? "Past the business-day SLA and not yet answered."
              : "Not yet answered; the business-day SLA is running."}
          </Notice>
        )}

        <Panel title="The person">
          <Fields>
            <Field label="Email">
              <span className="font-mono text-xs tabular-nums">
                {enquiry.email ?? "—"}
              </span>
            </Field>
            <Field label="Phone">
              <span className="font-mono text-xs tabular-nums">
                {enquiry.phone ?? "—"}
              </span>
            </Field>
            <Field label="School, as typed">
              {enquiry.school_name_as_typed ?? "—"}
            </Field>
            <Field label="State">{enquiry.state ?? "—"}</Field>
            <Field label="Year level">
              {enquiry.year_level_interest ?? "—"}
            </Field>
            <Field label="Program">{enquiry.program_interest ?? "—"}</Field>
            <Field label="Programs comms">
              {enquiry.consent_programs_comms ? "Opted in" : "No"}
            </Field>
            <Field label="First response">
              <span className="font-mono text-xs tabular-nums">
                {stamp(enquiry.first_response_at)}
              </span>
            </Field>
            {enquiry.status === "closed" && (
              <Field label="Closed">
                <span className="font-mono text-xs tabular-nums">
                  {stamp(enquiry.closed_at)}
                </span>
                {enquiry.closed_reason
                  ? ` · ${enquiry.closed_reason.replace(/_/g, " ")}`
                  : ""}
              </Field>
            )}
          </Fields>
        </Panel>

        <Panel title="The school">
          {matched_school ? (
            <p className="text-secondary text-sm">
              Matched to{" "}
              <Link
                href={adminSchoolPath(matched_school.id)}
                className="text-brand-secondary hover:text-brand-secondary_hover"
              >
                {matched_school.name}
              </Link>{" "}
              ({enquiry.match_confidence})
              {matched_school.has_history
                ? " — a school we know; a new person there"
                : " — no history with us"}
              .
            </p>
          ) : (
            <p className="text-tertiary text-sm">No school matched yet.</p>
          )}
          {candidates.length > 0 && (
            <ul className="divide-secondary flex flex-col divide-y">
              {candidates.map((c) => (
                <li
                  key={c.salesforce_account_id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span className="text-secondary text-sm">
                    {c.name}{" "}
                    <span className="text-tertiary">
                      ({c.confidence}
                      {c.has_history ? ", has history" : ""})
                    </span>
                  </span>
                  <Button
                    size="sm"
                    color="secondary"
                    onPress={() => confirm(c.salesforce_account_id)}
                  >
                    Confirm
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <div className="flex flex-wrap gap-2">
          {!enquiry.first_response_at && (
            <Button size="md" color="primary" onPress={answer}>
              Mark answered
            </Button>
          )}
          {enquiry.status !== "closed" && (
            <Button size="md" color="secondary" onPress={close}>
              Close
            </Button>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
